import React, { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface UiPageHeaderProps {
  icon: IconDefinition;
  title: string;
  subtitle?: string;
  /** Default: uses --theme-primary-gradient CSS var */
  gradient?: string;
  /** Color for decorative blobs and ping animation. Default: --theme-primary */
  accentColor?: string;
  /** Slot rendered below subtitle (e.g. LiveViewToggle) */
  below?: ReactNode;
  /** Slot on the right side (e.g. filter controls) */
  actions?: ReactNode;
}

const UiPageHeader: React.FC<UiPageHeaderProps> = ({
  icon,
  title,
  subtitle,
  gradient = 'var(--theme-primary-gradient)',
  accentColor = 'var(--theme-primary)',
  below,
  actions,
}) => (
  <div
    className="relative overflow-hidden rounded-2xl mb-6 p-6 sm:p-8"
    style={{ background: gradient }}
  >
    <div className="absolute inset-0 overflow-hidden">
      <div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl"
        style={{ backgroundColor: accentColor, opacity: 0.3 }}
      />
      <div
        className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full blur-3xl"
        style={{ backgroundColor: accentColor, opacity: 0.2 }}
      />
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />
    </div>

    <div
      className={`relative z-10 flex flex-col gap-6 ${
        actions ? 'lg:flex-row lg:items-center justify-between' : 'items-start'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="relative">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
              backdropFilter: 'blur(10px)',
              boxShadow:
                '0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
            <FontAwesomeIcon icon={icon} className="text-2xl text-white" />
          </div>
          <div
            className="absolute inset-0 rounded-2xl animate-ping"
            style={{ backgroundColor: accentColor, opacity: 0.3 }}
          />
        </div>

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-white/70 text-sm mt-1 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              {subtitle}
            </p>
          )}
          {below}
        </div>
      </div>

      {actions && <div>{actions}</div>}
    </div>
  </div>
);

export default UiPageHeader;
